import {
  Flex,
  HStack,
  Text,
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuDivider,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FaEllipsisV, FaFolder, FaPlus } from 'react-icons/fa';
import { MdOutlineSmartToy } from 'react-icons/md';
import { DtType } from '../../generated/graphql';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanLabel, HelivanLabelVariant } from '../helivan/HelivanLabel';
import { AddToCollectionModal } from '../MyAgents/AddToCollectionModal';
import { CreateNewProjectModal } from '../Projects/CreateNewProjectModal';

interface DigitalTwinCardProps {
  id: string;
  name: string;
  projectLabels: { variant: HelivanLabelVariant; name: string }[];
  industry: DtType;
  roleTitle?: string;
  subtitle?: string;
  projectCount: number;
}

export const DigitalTwinCard = ({
  id,
  name,
  projectLabels,
  industry,
  roleTitle,
  subtitle,
  projectCount,
}: DigitalTwinCardProps) => {
  const navigate = useNavigate();
  const addToCollectionModal = useDisclosure();
  const createProjectModal = useDisclosure();

  const handleCardClick = () => {
    navigate(`/personas/${id}`);
  };

  return (
    <>
      <AddToCollectionModal
        isOpen={addToCollectionModal.isOpen}
        onClose={addToCollectionModal.onClose}
        agentId={id}
        agentName={name}
        agentType="digitalTwin"
      />
      <CreateNewProjectModal
        isOpen={createProjectModal.isOpen}
        onClose={createProjectModal.onClose}
        hasTokens={true}
        preSelectedDigitalTwinId={id}
        preSelectedDigitalTwinName={name}
        preSelectedDigitalTwinType={industry}
      />

      <Flex
        width="full"
        minWidth="240px"
        maxWidth="300px"
        flexDirection="column"
        bg="white"
        borderRadius="12px"
        overflow="hidden"
        boxShadow="0 1px 3px rgba(0,0,0,0.06)"
        border="1px solid"
        borderColor={HelivanColor.border.primary}
        _hover={{
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0,71,125,0.1)',
          borderColor: HelivanColor.text.helivanBlue,
        }}
        transition="all 0.15s ease"
        cursor="pointer"
        onClick={handleCardClick}
      >
        {/* Header */}
        <Flex
          px="14px"
          py="10px"
          bg={HelivanColor.background.secondary}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor={HelivanColor.border.primary}
        >
          <HStack spacing={2}>
            <MdOutlineSmartToy size={12} color={HelivanColor.text.helivanBlue} />
            <Text fontSize="11px" fontWeight={500} color={HelivanColor.text.blueMid}>
              Persona
            </Text>
          </HStack>
          <HStack spacing={2}>
            <Text fontSize="10px" color={HelivanColor.text.blueMid}>
              {projectCount} project{projectCount !== 1 ? 's' : ''}
            </Text>
          </HStack>
        </Flex>

        <Flex flexDirection="column" p="14px" gap="10px">
          {/* Name, Role and Actions */}
          <Flex width="full" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <Flex direction="column" flex="1" minWidth={0}>
              <Text
                fontSize="15px"
                fontWeight={600}
                color={HelivanColor.text.blueDark}
                lineHeight="1.3"
                noOfLines={1}
              >
                {name}
              </Text>
              {roleTitle && (
                <Text fontSize="11px" color={HelivanColor.text.blueMid} fontWeight={500} noOfLines={1} mt="2px">
                  {roleTitle}
                </Text>
              )}
              {subtitle && (
                <Text fontSize="10px" color={HelivanColor.text.gray} noOfLines={1} mt="2px">
                  {subtitle}
                </Text>
              )}
            </Flex>

            <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
              <Menu isLazy strategy="fixed" placement="bottom-end">
                <MenuButton
                  as={IconButton}
                  aria-label="Options"
                  icon={<FaEllipsisV size={12} />}
                  size="xs"
                  variant="ghost"
                  minW="28px"
                  h="28px"
                  borderRadius="6px"
                  _hover={{ bg: HelivanColor.background.secondary }}
                />
                <Portal>
                  <MenuList
                    fontSize="13px"
                    onClick={(e) => e.stopPropagation()}
                    zIndex={1400}
                    boxShadow="0 4px 20px rgba(0,0,0,0.15)"
                    borderRadius="8px"
                    border="1px solid"
                    borderColor={HelivanColor.border.primary}
                    py={2}
                    minW="180px"
                  >
                    <MenuItem
                      icon={<FaPlus size={12} />}
                      onClick={() => createProjectModal.onOpen()}
                      py={2}
                      px={3}
                      _hover={{ bg: HelivanColor.background.secondary }}
                    >
                      Create Project
                    </MenuItem>
                    <MenuDivider my={1} borderColor={HelivanColor.border.primary} />
                    <MenuItem
                      icon={<FaFolder size={12} />}
                      onClick={() => addToCollectionModal.onOpen()}
                      py={2}
                      px={3}
                      _hover={{ bg: HelivanColor.background.secondary }}
                    >
                      Add to Collection
                    </MenuItem>
                  </MenuList>
                </Portal>
              </Menu>
            </HStack>
          </Flex>

          {/* Project Labels */}
          {projectLabels.length > 0 && (
            <Flex gap="4px" flexWrap="wrap">
              {projectLabels.slice(0, 2).map((label, idx) => (
                <HelivanLabel
                  key={idx}
                  label={label.name}
                  variant={label.variant}
                  size="sm"
                />
              ))}
              {projectLabels.length > 2 && (
                <Box
                  px="6px"
                  py="2px"
                  borderRadius="4px"
                  bg={HelivanColor.background.secondary}
                >
                  <Text fontSize="10px" color={HelivanColor.text.blueMid}>
                    +{projectLabels.length - 2}
                  </Text>
                </Box>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
    </>
  );
};
